import { createContext, useCallback, useState } from "react";
import { Dropdown } from "../components/Dropdown";
import { LayoutChangeEvent } from "react-native";
import RootOverlayHost from "../components/RootOverlayHost";

export type DropdownComponentType = React.ComponentType<{ fadeOut: () => void }>;

export type DropdownConfigType = {
    component?: DropdownComponentType;
    position?: {
        visible?: boolean;
        x: number;
        y: number;
        width: number;
        maxHeight: number;
        pressX?: number;
        pressY?: number;
        dropdownPosition: "left" | "right";
    } | null;
    uniqueSelection?: boolean;
    keepOpen?: boolean;
    onBlur?: () => void;
    onLayout?: (e: LayoutChangeEvent) => void;
};

interface DropdownContextInterface {
    config: DropdownConfigType;
    setDropdownConfig: React.Dispatch<React.SetStateAction<DropdownConfigType>>;
}

export const DropdownManagerContext = createContext<DropdownContextInterface>({
    config: { component: undefined, position: null, keepOpen: false },
    setDropdownConfig: () => {},
});

type DropdownManagerProps = {
    children: React.ReactNode;
};

const DropdownManager = ({ children }: DropdownManagerProps) => {
    const [config, setDropdownConfig] = useState<DropdownConfigType>({
        component: undefined,
        position: null,
        keepOpen: false,
    });

    const closeDropdown = useCallback(() => {
        setDropdownConfig({ component: undefined, position: null, keepOpen: false });
    }, []);

    return (
        <DropdownManagerContext.Provider
            value={{
                config,
                setDropdownConfig: (e) => {
                    if (typeof e === "function") {
                        setDropdownConfig(e);
                    } else if (!config.position) {
                        setDropdownConfig({ ...config, ...e });
                    }
                },
            }}
        >
            {children}
            {config.component && config.position && (
                // The dropdown is mounted at the root, so on iOS it would paint BEHIND a presented
                // native modal (same root view controller). RootOverlayHost lifts it into a
                // FullWindowOverlay above the whole VC stack, and is a no-op off iOS — so this stays
                // platform-agnostic. `blocking` lets the dropdown's own OutsidePressHandler catch the
                // outside tap that closes it.
                <RootOverlayHost pointerMode="blocking">
                    <Dropdown
                        onClose={closeDropdown}
                        onBlur={config.onBlur}
                        onLayout={config.onLayout}
                        position={config.position}
                        keepOpen={config.keepOpen}
                        uniqueSelection={config.uniqueSelection}
                        component={config.component}
                    />
                </RootOverlayHost>
            )}
        </DropdownManagerContext.Provider>
    );
};

export default DropdownManager;
